import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, filter, switchMap, tap } from 'rxjs/operators';
import { SharedActions } from '@states/shared/shared.action-types';
import { AuthenticationActions } from '@states/authentication/authentication.action-types';
import { AuthenticationService } from '../../authentication/authentication.service';
import { from, of, share } from 'rxjs';
import { GlobalKeyboardService } from '../../shared/global-keyboard.service';
import * as OrganizationActions from '@states/organization/organization.actions';
import { SearchableActions } from '@states/searchable/searchable.action-types';

@Injectable()
export class AuthenticationEffects {
  authenticated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthenticationActions.Authenticated),
        tap((action) => {
          this.authenticationService.storeSession({
            idToken: action.auth.idToken!,
            accessToken: action.auth.accessToken!,
            expiresAt: action.auth.expiresAt!,
            authProviderId: action.auth.authProviderId!,
            autoLogout: action.auth.autoLogout,
          });
        }),
        tap(() => {
          this.authenticationService.connectSocket();
        }),
        switchMap((action) => {
          if (action?.isSilent) {
            return [SharedActions.doNothing()];
          }

          return [SharedActions.startLoadRequiredData()];
        }),
      ),
    { dispatch: true },
  );

  initGlobalKeyboardOnOrganizationLoaded$ = createEffect(
    () => this.actions$.pipe(
      ofType(OrganizationActions.setActiveOrganization, OrganizationActions.updateActiveOrganizationSuccess),
      filter(({ activeOrganization }) => activeOrganization?.enableGlobalSearch ?? true),
      switchMap(() => {
        this.globalKeyboardService.init();
        return [SearchableActions.SearchableSync()];
      }),
    ),
  );

  public logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthenticationActions.Logout),
      exhaustMap(({ params }) =>
        from(this.authenticationService.logout(params)).pipe(
          switchMap(() => of(AuthenticationActions.LogoutSuccess())),
          catchError(() => of(AuthenticationActions.LogoutSuccess())),
        ),
      ),
      share(),
    ),
  );

  constructor(
    private actions$: Actions,
    private authenticationService: AuthenticationService,
    private globalKeyboardService: GlobalKeyboardService,
  ) {}
}
